import type { Rng } from './rng';
import type { Tunables } from './tunables';
import type { DriveState, PlayType, Shell } from './types';

export function newDrive(t: Tunables): DriveState {
  return { ballOn: t.drive.startBallOn, down: 1, toGo: t.drive.yardsToGain, plays: 0 };
}

export type DriveEvent = 'none' | 'firstDown' | 'touchdown' | 'turnoverOnDowns' | 'interception' | 'safety';

export interface DriveUpdate {
  state: DriveState;
  event: DriveEvent;
  /** True when the drive is over (score or turnover). */
  ended: boolean;
}

/** Advances down and distance after a play. gain is yards from the line of scrimmage. */
export function applyPlay(prev: DriveState, gain: number, opts: { intercepted: boolean }, t: Tunables): DriveUpdate {
  const plays = prev.plays + 1;
  if (opts.intercepted) return { state: { ...prev, plays }, event: 'interception', ended: true };
  const ballOn = prev.ballOn + gain;
  if (ballOn >= 100) return { state: { ballOn: 100, down: 1, toGo: 0, plays }, event: 'touchdown', ended: true };
  if (ballOn <= 0) return { state: { ballOn: 0, down: 1, toGo: 0, plays }, event: 'safety', ended: true };
  if (gain >= prev.toGo) {
    return {
      state: { ballOn, down: 1, toGo: Math.min(t.drive.yardsToGain, 100 - ballOn), plays },
      event: 'firstDown',
      ended: false,
    };
  }
  if (prev.down >= t.drive.maxDowns) return { state: { ballOn, down: prev.down, toGo: prev.toGo - gain, plays }, event: 'turnoverOnDowns', ended: true };
  return { state: { ballOn, down: prev.down + 1, toGo: prev.toGo - gain, plays }, event: 'none', ended: false };
}

/** "1st & 10", "3rd & Goal". */
export function downAndDistance(s: DriveState): string {
  const ord = ['1st', '2nd', '3rd', '4th'][s.down - 1] ?? `${s.down}th`;
  const goal = s.ballOn + s.toGo >= 100;
  return `${ord} & ${goal ? 'Goal' : Math.max(1, Math.round(s.toGo))}`;
}

/** "OWN 25", "50", "OPP 30". */
export function fieldPosition(ballOn: number): string {
  const b = Math.round(ballOn);
  if (b === 50) return '50';
  return b < 50 ? `OWN ${b}` : `OPP ${100 - b}`;
}

/** The coach's pick (accent outline on the card). Simple situational rule, AI-concluded. */
export function suggestPlayType(s: DriveState, t: Tunables): PlayType {
  if (s.toGo <= t.coverage.shortYardageToGo) return 'short';
  if (s.down >= 3 && s.toGo >= t.coverage.longToGo) return 'deep';
  if (s.down === 1) return 'playaction';
  return 'short';
}

/** Defensive shell for this snap, weighted by down and distance (GDD 6.7). */
export function pickShell(s: DriveState, rng: Rng, t: Tunables): Shell {
  const c = t.coverage;
  const table = s.toGo <= c.shortYardageToGo ? c.weights.shortYardage : s.toGo >= c.longToGo ? c.weights.long : c.weights.default;
  return rng.weighted(table as Record<Shell, number>);
}
